import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation, Link } from "react-router-dom";
import { Button, Card, Alert, Badge, Modal } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import { CoTeacherCard, DETAIL_PAGE } from "./CoTeacherCard";

const PageContainer = styled.div`
  max-width: 900px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 24px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledCard = styled(Card)`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background: ${colors.milkGlass200};
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.milkGlass400};
  font-weight: 600;
  color: ${colors.nightlyWoods900};
`;

const DetailRow = styled.div`
  display: flex;
  padding: 12px 20px;
  border-bottom: 1px solid ${colors.milkGlass200};
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  flex: 0 0 180px;
  color: ${colors.nightlyWoods600};
  font-weight: 500;
`;

const DetailValue = styled.div`
  flex: 1;
  color: ${colors.nightlyWoods900};
`;

const RoleBadge = styled(Badge)`
  margin-right: 8px;
  margin-bottom: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const CodeCard = styled.div`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const CodeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StandardName = styled.div`
  font-weight: 600;
  color: ${colors.nightlyWoods900};
`;

const StandardCode = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.orange500};
`;

const NoStandard = styled.div`
  font-weight: 600;
  color: ${colors.nightlyWoods600};
`;

const CodeMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${colors.nightlyWoods600};
`;

// Mirrors the educator dashboard's session-length buckets so the admin view
// shows the same human-readable label (e.g. "15-20 Minutes").
const SESSION_LENGTH_BUCKETS = [
  { value: 12.5, label: "10-15 Minutes" },
  { value: 17.5, label: "15-20 Minutes" },
  { value: 22.5, label: "20-25 Minutes" },
  { value: 27.5, label: "25-30 Minutes" },
  { value: 32.5, label: "30-35 Minutes" },
];

const sessionLengthLabel = (mins) => {
  if (mins == null) return null;
  const exact = SESSION_LENGTH_BUCKETS.find((opt) => opt.value === mins);
  if (exact) return exact.label;
  const bucket = SESSION_LENGTH_BUCKETS.find((opt) => mins <= opt.value + 2.5);
  return bucket ? bucket.label : `${mins} Minutes`;
};

const ROLE_OPTIONS = [
  { value: "superuser", label: "Superuser" },
  { value: "oko_staff", label: "OKO Staff" },
  { value: "client_admin", label: "Client Admin" },
  { value: "school_admin", label: "School Admin" },
  { value: "content_manager", label: "Content Manager" },
  { value: "educator", label: "Educator" },
  { value: "student", label: "Student" },
];

const EDUCATOR_TYPES = {
  teacher: "Teacher",
  math_specialist: "Math Specialist",
  school_administrator: "School Administrator",
  district_administrator: "District Administrator",
};

export const UserDetail = () => {
  const { id } = useParams();
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedIdRef = React.useRef(null);

  // Admin permission flags fetched from /admin/me
  const [adminUserData, setAdminUserData] = useState(null);

  // Drop student confirmation modal state
  const [showDropModal, setShowDropModal] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [dropError, setDropError] = useState(null);
  const [dropSuccess, setDropSuccess] = useState(false);
  const [dropResult, setDropResult] = useState(null);

  const backToListUrl = generateRoutePath(constants.USER_ADMIN_ROUTE_NAME) + (location.state?.listQuery || "");

  // Fetch /admin/me once to determine if the viewer is a superuser
  useEffect(() => {
    request("admin/me", { method: "GET" })
      .then((data) => setAdminUserData(data))
      .catch(() => setAdminUserData(null));
  }, []);

  useEffect(() => {
    if (userLoading) return;

    if (!authUserData || authUserData.isAnonymous) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return;
    }

    if (fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;

    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await request(`admin/users/${id}`, { method: "GET" });
        setUser(response);
      } catch (err) {
        console.error("Failed to fetch user:", err);
        if (err.status === 403) {
          setError("You do not have permission to view this user.");
        } else if (err.status === 404) {
          setError("User not found.");
        } else {
          setError("Failed to load user. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, userLoading, history, authUserData]);

  const handleEdit = () => {
    history.push(generateRoutePath(constants.USER_ADMIN_EDIT_ROUTE_NAME, { id }));
  };

  const handleDropConfirm = async () => {
    setDropping(true);
    setDropError(null);
    try {
      const result = await request(`admin/users/${id}/data`, { method: "DELETE" });
      setDropResult(result);
      setDropSuccess(true);
      setShowDropModal(false);
    } catch (err) {
      console.error("Failed to drop student data:", err);
      const detail = err?.data?.detail || "Failed to delete student data. Please try again.";
      setDropError(detail);
    } finally {
      setDropping(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatRole = (role) => {
    const found = ROLE_OPTIONS.find(r => r.value === role);
    return found ? found.label : role;
  };

  const getRoleBadgeVariant = (role) => {
    if (role === "superuser") return "danger";
    if (role === "oko_staff") return "warning";
    if (["client_admin", "school_admin"].includes(role)) return "info";
    if (role === "educator") return "primary";
    return "secondary";
  };

  const isStudent = user?.roles?.length === 1 && user.roles.includes("student");
  // Only superusers can drop students; can_access_user_admin is the superuser flag
  const canDrop = adminUserData?.can_access_user_admin && isStudent;

  const studentLabel = user?.display_name || user?.oko_public_id || id;

  if (userLoading || loading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <BackLink to={backToListUrl}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Users
        </BackLink>
        <Alert variant="danger">{error}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to={backToListUrl}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Users
      </BackLink>

      {dropSuccess && (
        <Alert variant="success" onClose={() => history.push(backToListUrl)} dismissible>
          <Alert.Heading>Student data permanently deleted.</Alert.Heading>
          <p>The user account no longer exists in Postgres or Firebase.</p>
          {dropResult && (
            <>
              <p style={{ marginBottom: 4 }}>
                <strong>BigQuery:</strong>{" "}
                {dropResult.bigquery_deleted
                  ? `Rows removed from event tables (pubsub_event_log: ${dropResult.bigquery_counts?.pubsub_event_log ?? 0}).`
                  : "BigQuery deletion encountered an error — check server logs and Sentry for details."}
              </p>
              {dropResult.recordings_to_handle && dropResult.recordings_to_handle.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Recordings requiring manual deletion</strong>
                  <p style={{ marginBottom: 4 }}>
                    The following sessions have recording objects in GCS that must be deleted manually
                    (recordings are shared group audio/video and cannot be auto-deleted):
                  </p>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {dropResult.recordings_to_handle.map((sessionId) => (
                      <li key={sessionId}>
                        <code>{sessionId}</code>
                        {" — "}
                        <span style={{ fontSize: 12, color: "#555" }}>
                          gs://{dropResult.recordings_bucket}/session-{sessionId}/
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </Alert>
      )}

      {dropError && (
        <Alert variant="danger" onClose={() => setDropError(null)} dismissible>
          {dropError}
        </Alert>
      )}

      <PageHeader>
        <PageTitle>{user?.display_name || user?.oko_public_id || "User Details"}</PageTitle>
        <HeaderActions>
          {canDrop && (
            <Button
              variant="outline-danger"
              onClick={() => setShowDropModal(true)}
              data-testid="drop-student-btn"
            >
              <FontAwesomeIcon icon={faTrash} /> Drop student
            </Button>
          )}
          <Button
            onClick={handleEdit}
            style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
          >
            <FontAwesomeIcon icon={faEdit} /> Edit
          </Button>
        </HeaderActions>
      </PageHeader>

      <StyledCard>
        <CardHeader>Basic Information</CardHeader>
        <Card.Body style={{ padding: 0 }}>
          <DetailRow>
            <DetailLabel>Display Name</DetailLabel>
            <DetailValue>{user?.display_name || "-"}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Email</DetailLabel>
            <DetailValue>{user?.email || "-"}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>OKO Public ID</DetailLabel>
            <DetailValue><code>{user?.oko_public_id || "-"}</code></DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>User ID</DetailLabel>
            <DetailValue><code>{user?.id}</code></DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Educator Type</DetailLabel>
            <DetailValue>
              {user?.educator_type ? EDUCATOR_TYPES[user.educator_type] || user.educator_type : "-"}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Record Sessions</DetailLabel>
            <DetailValue>{user?.record_sessions ? "Yes" : "No"}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Created</DetailLabel>
            <DetailValue>{formatDate(user?.created_at)}</DetailValue>
          </DetailRow>
        </Card.Body>
      </StyledCard>

      <StyledCard>
        <CardHeader>School</CardHeader>
        <Card.Body style={{ padding: 0 }}>
          <DetailRow>
            <DetailLabel>School Name</DetailLabel>
            <DetailValue>
              {user?.school_id ? (
                <Link to={generateRoutePath(constants.SCHOOL_ADMIN_DETAIL_ROUTE_NAME, { id: user.school_id })}>
                  {user?.school_name || "View School"}
                </Link>
              ) : "-"}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>School ID</DetailLabel>
            <DetailValue><code>{user?.school_id || "-"}</code></DetailValue>
          </DetailRow>
        </Card.Body>
      </StyledCard>

      <StyledCard>
        <CardHeader>Roles</CardHeader>
        <Card.Body>
          {user?.roles?.length > 0 ? (
            user.roles.map(role => (
              <RoleBadge key={role} bg={getRoleBadgeVariant(role)}>
                {formatRole(role)}
              </RoleBadge>
            ))
          ) : (
            <span style={{ color: colors.nightlyWoods500 }}>No roles assigned</span>
          )}
        </Card.Body>
      </StyledCard>

      <CoTeacherCard user={user} variant={DETAIL_PAGE} />

      <StyledCard>
        <CardHeader>Classes &amp; Group Codes</CardHeader>
        <Card.Body style={{ padding: 0 }}>
          {user?.classes?.length > 0 ? (
            user.classes.map(klass => {
              const details =
                klass.code_details?.length > 0
                  ? klass.code_details
                  : (klass.codes || []).map(code => ({ code }));
              return (
                <DetailRow key={klass.id} style={{ flexDirection: "column", alignItems: "stretch" }}>
                  <DetailLabel style={{ flex: "none", marginBottom: 8 }}>{klass.name}</DetailLabel>
                  {details.length > 0 ? (
                    details.map(detail => (
                      <CodeCard key={detail.code}>
                        <CodeCardHeader>
                          <div>
                            {detail.standard_code ? (
                              <>
                                <StandardCode>Standard - {detail.standard_code}</StandardCode>
                                <StandardName>{detail.standard_name || detail.standard_code}</StandardName>
                              </>
                            ) : (
                              <>
                                <StandardCode>No standard selected</StandardCode>
                                <NoStandard>Select a standard to get started</NoStandard>
                              </>
                            )}
                            {sessionLengthLabel(detail.session_length) && (
                              <CodeMeta>Session Length: {sessionLengthLabel(detail.session_length)}</CodeMeta>
                            )}
                          </div>
                          <code>{detail.code}</code>
                        </CodeCardHeader>
                      </CodeCard>
                    ))
                  ) : (
                    <span style={{ color: colors.nightlyWoods500 }}>No group codes</span>
                  )}
                </DetailRow>
              );
            })
          ) : (
            <DetailRow>
              <DetailValue>
                <span style={{ color: colors.nightlyWoods500 }}>No classes</span>
              </DetailValue>
            </DetailRow>
          )}
        </Card.Body>
      </StyledCard>

      {/* Drop Student Confirmation Modal */}
      <Modal show={showDropModal} onHide={() => !dropping && setShowDropModal(false)} centered>
        <Modal.Header closeButton={!dropping}>
          <Modal.Title>Permanently delete student data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You are about to permanently delete all covered information for{" "}
            <strong>{studentLabel}</strong>.
          </p>
          <Alert variant="danger" style={{ marginBottom: 0 }}>
            <strong>This cannot be undone.</strong> The following will be deleted:
            <ul style={{ marginTop: 8, marginBottom: 8 }}>
              <li>The student account (login, roles, classroom enrollments)</li>
              <li>Session participation records and connection history</li>
              <li>Session feedback and teacher report rows for this student</li>
              <li>Session replay event rows for this student</li>
              <li>BigQuery event rows keyed to this student (event log and staging views)</li>
            </ul>
            <p style={{ marginBottom: 4 }}>
              <strong>Recordings</strong> (GCS session audio/video) are shared across
              all session participants and will NOT be auto-deleted. Any sessions with
              recordings will be listed after deletion for manual follow-up.
            </p>
            <p style={{ marginBottom: 0 }}>
              Shared session content (group-level summaries, transcripts, compilation data)
              is de-identified once this identity row is removed and does not require deletion.
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDropModal(false)}
            disabled={dropping}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDropConfirm}
            disabled={dropping}
            data-testid="drop-student-confirm-btn"
          >
            {dropping ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Deleting...
              </>
            ) : (
              "Yes, permanently delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContainer>
  );
};
